import type { ReasonCode } from '../types'

export type ProcessingType = 'none' | 'process' | 'reprocess' | 'full_reprocess'

export type TriggerActionType =
  | 'graded_homework'
  | 'weekly_commitment_change'
  | 'target_date_change'
  | 'homework_row_regrade'
  | 'recommendation_status_change'
  | 'baseline_epoch_change'
  | 'seed_values_change'
  | 'target_proficiency_change'
  | 'target_company_change'
  | 'baseline_level_change'
  | 'graph_json_change'

const PROCESSING_SEVERITY: Record<ProcessingType, number> = {
  none: 0,
  process: 1,
  reprocess: 2,
  full_reprocess: 3,
}

const ACTION_TO_PROCESSING: Record<TriggerActionType, ProcessingType> = {
  graded_homework: 'process',
  weekly_commitment_change: 'process',
  target_date_change: 'process',
  recommendation_status_change: 'process',
  homework_row_regrade: 'reprocess',
  baseline_epoch_change: 'full_reprocess',
  seed_values_change: 'full_reprocess',
  target_proficiency_change: 'full_reprocess',
  target_company_change: 'full_reprocess',
  baseline_level_change: 'full_reprocess',
  graph_json_change: 'none',
}

const REASON_TO_ACTION: Partial<Record<ReasonCode, TriggerActionType>> = {
  attempt_reviewed: 'graded_homework',
  baseline_epoch_change: 'baseline_epoch_change',
  graph_json_saved: 'graph_json_change',
  coach_seed: 'seed_values_change',
  coach_seed_override: 'seed_values_change',
  threshold_change: 'target_proficiency_change',
  rollup_weight_change: 'target_proficiency_change',
  subtopic_enable_toggle: 'target_proficiency_change',
  topic_inclusion_change: 'target_proficiency_change',
  preset_applied: 'target_proficiency_change',
  target_company_change: 'target_company_change',
  baseline_level_change: 'baseline_level_change',
  weekly_commitment_change: 'weekly_commitment_change',
  target_date_change: 'target_date_change',
}

export function getProcessingTypeForAction(
  action: TriggerActionType,
): ProcessingType {
  return ACTION_TO_PROCESSING[action]
}

export function getTriggerActionForReasonCode(
  reasonCode: ReasonCode,
): TriggerActionType | null {
  return REASON_TO_ACTION[reasonCode] ?? null
}

export interface PendingProcessingSummary {
  processingTypes: ProcessingType[]
  highestSeverityType: ProcessingType
  summaryLabel: string
}

function dedupeAndSort(types: ProcessingType[]): ProcessingType[] {
  return [...new Set(types)].sort(
    (a, b) => PROCESSING_SEVERITY[b] - PROCESSING_SEVERITY[a],
  )
}

export function summarizePendingProcessing(
  actions: TriggerActionType[],
): PendingProcessingSummary {
  if (actions.length === 0) {
    return {
      processingTypes: [],
      highestSeverityType: 'process',
      summaryLabel: 'No processing required.',
    }
  }

  const mappedTypes = dedupeAndSort(actions.map(getProcessingTypeForAction))
  const hasFullReprocess = mappedTypes.includes('full_reprocess')
  const normalizedTypes: ProcessingType[] = hasFullReprocess
    ? ['full_reprocess']
    : mappedTypes
  const highestSeverityType = normalizedTypes[0] ?? 'process'

  if (hasFullReprocess) {
    return {
      processingTypes: normalizedTypes,
      highestSeverityType,
      summaryLabel:
        'Full reprocess required (covers all pending historical and new updates).',
    }
  }

  if (normalizedTypes.includes('reprocess')) {
    return {
      processingTypes: normalizedTypes,
      highestSeverityType,
      summaryLabel: normalizedTypes.includes('process')
        ? 'Reprocess plus process required (historical corrections run before new processing).'
        : 'Reprocess required for pending historical corrections and edits.',
    }
  }

  if (normalizedTypes.length === 1 && normalizedTypes[0] === 'none') {
    return {
      processingTypes: normalizedTypes,
      highestSeverityType: 'none',
      summaryLabel:
        'Graph updated. Run processing to acknowledge (no FSRS replay).',
    }
  }

  if (normalizedTypes.includes('process')) {
    return {
      processingTypes: normalizedTypes,
      highestSeverityType,
      summaryLabel: 'Processing required for pending forward-only updates.',
    }
  }

  return {
    processingTypes: normalizedTypes,
    highestSeverityType,
    summaryLabel:
      'Graph updated. Run processing to acknowledge (no FSRS replay).',
  }
}
